/**
 * Remotion composition templates for the Sibrik local editor.
 *
 * These are code generators: they take structured input (clips, text, audio)
 * and produce valid .tsx source that Remotion Studio can render.
 *
 * The composition ID is always "SibrikComposition" so render commands
 * can reference it predictably.
 */

export interface VideoClip {
  /** Relative path inside the project's public/ folder, e.g. "media/clip-1.mp4" */
  localPath: string;
  /** Start frame (0-based) */
  startFrame: number;
  /** Duration in frames */
  durationFrames: number;
  /** Media type — "video" or "image" */
  type?: 'video' | 'image';
}

export interface TextLayer {
  text: string;
  startFrame: number;
  durationFrames: number;
  fontSize?: number;
  color?: string;
  position?: 'top' | 'center' | 'bottom';
}

export interface AudioTrack {
  localPath: string;
  startFrame: number;
  durationFrames: number;
  volume?: number;
}

export interface CompositionState {
  fps: number;
  width: number;
  height: number;
  durationFrames: number;
  clips: VideoClip[];
  textLayers: TextLayer[];
  audioTracks: AudioTrack[];
}

const positionToStyle: Record<string, string> = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
};

/**
 * Generates the root entry file (src/index.ts) that registers the composition.
 * Remotion Studio reads this to know what to render.
 */
export function generateEntryFile(state: CompositionState): string {
  return `import { Composition, registerRoot } from 'remotion';
import { SibrikComposition } from './SibrikComposition';

const RemotionRoot: React.FC = () => {
  return (
    <Composition
      id="SibrikComposition"
      component={SibrikComposition}
      durationInFrames={${state.durationFrames}}
      fps={${state.fps}}
      width={${state.width}}
      height={${state.height}}
    />
  );
};

registerRoot(RemotionRoot);
`;
}

/**
 * Generates the main composition component (.tsx) from the current state.
 * This file is overwritten every time the user adds/removes clips, text, or audio.
 */
export function generateComposition(state: CompositionState): string {
  const videoSequences = state.clips
    .map((clip) => {
      const isImage = clip.type === 'image' || /\.(png|jpe?g|webp|gif|bmp|svg|avif)$/i.test(clip.localPath);
      const mediaTag = isImage ? 'Img' : 'OffthreadVideo';
      return `      <Sequence from={${clip.startFrame}} durationInFrames={${clip.durationFrames}}>
        <${mediaTag} src={staticFile("${clip.localPath}")} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </Sequence>`;
    })
    .join('\n');

  const textSequences = state.textLayers
    .map((layer) => {
      const fs = layer.fontSize ?? 88;
      const color = layer.color ?? 'white';
      const justify = positionToStyle[layer.position ?? 'center'] ?? 'center';
      return `      <Sequence from={${layer.startFrame}} durationInFrames={${layer.durationFrames}}>
        <AbsoluteFill style={{ justifyContent: '${justify}', alignItems: 'center' }}>
          <div style={{
            fontSize: ${fs},
            color: '${color}',
            fontWeight: 'bold',
            textShadow: '0 4px 12px rgba(0,0,0,0.6)',
            opacity: interpolate(frame, [0, 15], [0, 1], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }),
          }}>
            ${escapeJsxText(layer.text)}
          </div>
        </AbsoluteFill>
      </Sequence>`;
    })
    .join('\n');

  const audioElements = state.audioTracks
    .map(
      (track) => `      <Sequence from={${track.startFrame}} durationInFrames={${track.durationFrames}}>
        <Audio src={staticFile("${track.localPath}")} volume={${track.volume ?? 1}} />
      </Sequence>`,
    )
    .join('\n');

  const needsFrame = state.textLayers.length > 0;
  const frameHook = needsFrame ? 'const frame = useCurrentFrame();' : '';
  const hasImages = state.clips.some((c) => c.type === 'image' || /\.(png|jpe?g|webp|gif|bmp|svg|avif)$/i.test(c.localPath));
  const imports = [
    'AbsoluteFill',
    'Sequence',
    'staticFile',
    'OffthreadVideo',
    ...(hasImages ? ['Img'] : []),
    ...(state.audioTracks.length > 0 ? ['Audio'] : []),
    ...(needsFrame ? ['useCurrentFrame', 'interpolate'] : []),
  ].join(', ');

  return `import { ${imports} } from 'remotion';

export const SibrikComposition: React.FC = () => {
  ${frameHook}
  return (
    <AbsoluteFill style={{ backgroundColor: 'black' }}>
${videoSequences || '      {/* No video clips yet — use add_video_clip to add some */}'}
${textSequences}
${audioElements}
    </AbsoluteFill>
  );
};
`;
}

/**
 * Generates a minimal package.json for the Remotion project on disk.
 */
export function generateProjectPackageJson(): string {
  return JSON.stringify(
    {
      name: 'sibrik-editor-project',
      version: '1.0.0',
      private: true,
      scripts: {
        dev: 'remotion studio src/index.tsx',
        render: 'remotion render src/index.tsx SibrikComposition out/video.mp4',
      },
      dependencies: {
        remotion: '^4.0.0',
        '@remotion/cli': '^4.0.0',
        '@remotion/bundler': '^4.0.0',
        '@remotion/renderer': '^4.0.0',
      },
    },
    null,
    2,
  );
}

function escapeJsxText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
